/**
 * AI Thinking Animation Component
 * Displays animated sine waves with clouds and light pulses
 * representing AI processing with rotating scripture verses
 */

import { useState, useEffect } from 'react';
import { usePrefersReducedMotion } from '../hooks/useMediaQuery';

interface AIThinkingAnimationProps {
  className?: string;
}

const scriptures = [
  {
    text: "Whatever your hand finds to do, do it with your might",
    reference: "Ecclesiastes 9:10"
  },
  {
    text: "Do you see someone skilled in their work? They will serve before kings",
    reference: "Proverbs 22:29"
  },
  {
    text: "In all toil there is profit, but mere talk tends only to poverty",
    reference: "Proverbs 14:23"
  },
  {
    text: "And whatever you do, do it heartily, as to the Lord",
    reference: "Colossians 3:23"
  },
  {
    text: "Every good gift and every perfect gift is from above",
    reference: "James 1:17"
  },
  {
    text: "The Lord has filled him with the Spirit of God, with skill and understanding",
    reference: "Exodus 35:31"
  }
];

export default function AIThinkingAnimation({ className = '' }: AIThinkingAnimationProps) {
  const [currentScriptureIndex, setCurrentScriptureIndex] = useState(0);
  const [fadeState, setFadeState] = useState<'in' | 'out'>('in');
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null;

    const fadeInterval = setInterval(() => {
      setFadeState('out');
      timeoutId = setTimeout(() => {
        setCurrentScriptureIndex((prev) => (prev + 1) % scriptures.length);
        setFadeState('in');
      }, 1500); // Wait for fade out before changing
    }, 8000); // Change scripture every 8 seconds

    return () => {
      clearInterval(fadeInterval);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  const currentScripture = scriptures[currentScriptureIndex];

  return (
    <div className={`flex flex-col items-center justify-center gap-4 w-full ${className}`}>
      {/* Sine Waves Animation or Reduced Motion Alternative */}
      <div className="w-full relative overflow-hidden rounded-lg" style={{ height: '200px' }}>
        {prefersReducedMotion ? (
          /* Simple static indicator for reduced motion users */
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-b from-dark-bg/50 to-dark-card/80 rounded-lg">
            <div className="flex flex-col items-center gap-3">
              <div className="w-12 h-12 border-3 border-accent-blue border-t-transparent rounded-full" />
              <p className="text-sm text-dark-text-muted">Processing your request...</p>
            </div>
          </div>
        ) : (
          <svg
          width="100%"
          height="200"
          viewBox="0 0 1200 200"
          preserveAspectRatio="xMidYMid slice"
          xmlns="http://www.w3.org/2000/svg"
          className="sine-wave-animation"
        >
          <defs>
            {/* Gradients for waves */}
            <linearGradient id="waveGradient1" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#464bba" stopOpacity="0.6" />
              <stop offset="50%" stopColor="#ee6c4c" stopOpacity="0.6" />
              <stop offset="100%" stopColor="#464bba" stopOpacity="0.6" />
            </linearGradient>
            <linearGradient id="waveGradient2" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#ee6c4c" stopOpacity="0.5" />
              <stop offset="50%" stopColor="#464bba" stopOpacity="0.5" />
              <stop offset="100%" stopColor="#ee6c4c" stopOpacity="0.5" />
            </linearGradient>
            <linearGradient id="waveGradient3" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#464bba" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#ee6c4c" stopOpacity="0.4" />
            </linearGradient>

            {/* Cloud gradient */}
            <radialGradient id="cloudGradient">
              <stop offset="0%" stopColor="#ffffff" stopOpacity="0.3" />
              <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
            </radialGradient>

            {/* Light burst gradient */}
            <radialGradient id="lightBurst">
              <stop offset="0%" stopColor="#ffffff" stopOpacity="0.8" />
              <stop offset="30%" stopColor="#ee6c4c" stopOpacity="0.4" />
              <stop offset="70%" stopColor="#464bba" stopOpacity="0.2" />
              <stop offset="100%" stopColor="#464bba" stopOpacity="0" />
            </radialGradient>
          </defs>

          {/* Sine Wave 1 - Large amplitude, slow */}
          <path className="wave wave-1" fill="none" stroke="url(#waveGradient1)" strokeWidth="3" strokeLinecap="round">
            <animate
              attributeName="d"
              dur="12s"
              repeatCount="indefinite"
              values="M0,100 Q150,50 300,100 T600,100 T900,100 T1200,100 T1500,100;
                      M-100,100 Q50,50 200,100 T500,100 T800,100 T1100,100 T1400,100;
                      M0,100 Q150,50 300,100 T600,100 T900,100 T1200,100 T1500,100"
            />
          </path>

          {/* Sine Wave 2 - Medium amplitude, medium speed */}
          <path className="wave wave-2" fill="none" stroke="url(#waveGradient2)" strokeWidth="2.5" strokeLinecap="round">
            <animate
              attributeName="d"
              dur="10s"
              repeatCount="indefinite"
              values="M0,120 Q100,90 200,120 T400,120 T600,120 T800,120 T1000,120 T1200,120;
                      M-50,120 Q50,90 150,120 T350,120 T550,120 T750,120 T950,120 T1150,120;
                      M0,120 Q100,90 200,120 T400,120 T600,120 T800,120 T1000,120 T1200,120"
            />
          </path>

          {/* Sine Wave 3 - Small amplitude, faster */}
          <path className="wave wave-3" fill="none" stroke="url(#waveGradient3)" strokeWidth="2" strokeLinecap="round">
            <animate
              attributeName="d"
              dur="8s"
              repeatCount="indefinite"
              values="M0,140 Q80,125 160,140 T320,140 T480,140 T640,140 T800,140 T960,140 T1120,140 T1280,140;
                      M-40,140 Q40,125 120,140 T280,140 T440,140 T600,140 T760,140 T920,140 T1080,140 T1240,140;
                      M0,140 Q80,125 160,140 T320,140 T480,140 T640,140 T800,140 T960,140 T1120,140 T1280,140"
            />
          </path>

          {/* Sine Wave 4 - Subtle, very slow */}
          <path className="wave wave-4" fill="none" stroke="url(#waveGradient1)" strokeWidth="1.5" strokeLinecap="round" opacity="0.6">
            <animate
              attributeName="d"
              dur="15s"
              repeatCount="indefinite"
              values="M0,80 Q200,60 400,80 T800,80 T1200,80 T1600,80;
                      M-150,80 Q50,60 250,80 T650,80 T1050,80 T1450,80;
                      M0,80 Q200,60 400,80 T800,80 T1200,80 T1600,80"
            />
          </path>

          {/* Cloud-like shapes */}
          <ellipse cx="200" cy="60" rx="80" ry="30" fill="url(#cloudGradient)" className="cloud cloud-1">
            <animate attributeName="cx" dur="20s" repeatCount="indefinite" values="200;1000;200" />
          </ellipse>
          <ellipse cx="600" cy="150" rx="100" ry="40" fill="url(#cloudGradient)" className="cloud cloud-2">
            <animate attributeName="cx" dur="25s" repeatCount="indefinite" values="600;-100;600" />
          </ellipse>
          <ellipse cx="900" cy="100" rx="70" ry="25" fill="url(#cloudGradient)" className="cloud cloud-3">
            <animate attributeName="cx" dur="22s" repeatCount="indefinite" values="900;1300;900" />
          </ellipse>

          {/* Light bursts / star bursts */}
          <circle cx="300" cy="100" r="40" fill="url(#lightBurst)" className="light-pulse light-1">
            <animate attributeName="r" dur="4s" repeatCount="indefinite" values="40;80;40" />
            <animate attributeName="opacity" dur="4s" repeatCount="indefinite" values="0.3;0.7;0.3" />
          </circle>
          <circle cx="700" cy="120" r="50" fill="url(#lightBurst)" className="light-pulse light-2">
            <animate attributeName="r" dur="5s" repeatCount="indefinite" values="50;90;50" />
            <animate attributeName="opacity" dur="5s" repeatCount="indefinite" values="0.2;0.6;0.2" />
          </circle>
          <circle cx="1000" cy="80" r="45" fill="url(#lightBurst)" className="light-pulse light-3">
            <animate attributeName="r" dur="6s" repeatCount="indefinite" values="45;85;45" />
            <animate attributeName="opacity" dur="6s" repeatCount="indefinite" values="0.25;0.65;0.25" />
          </circle>

          <style>{`
            .sine-wave-animation {
              background: linear-gradient(180deg, rgba(26, 26, 26, 0.5) 0%, rgba(42, 42, 42, 0.8) 100%);
            }

            .wave {
              filter: drop-shadow(0 0 8px rgba(70, 75, 186, 0.4));
            }

            .cloud {
              animation: cloudFloat 3s ease-in-out infinite;
            }
            .cloud-1 { animation-delay: 0s; }
            .cloud-2 { animation-delay: 1s; }
            .cloud-3 { animation-delay: 2s; }

            @keyframes cloudFloat {
              0%, 100% { transform: translateY(0); }
              50% { transform: translateY(-10px); }
            }

            .light-pulse {
              filter: blur(8px);
            }
          `}</style>
        </svg>
        )}
      </div>

      {/* Scripture Text with slower fade */}
      <div
        className={`text-center max-w-2xl px-4 transition-opacity duration-[1500ms] ${
          fadeState === 'in' ? 'opacity-100' : 'opacity-0'
        }`}
      >
        <p className="text-sm text-dark-text-muted italic mb-1">
          "{currentScripture.text}"
        </p>
        <p className="text-xs text-accent-blue font-semibold">
          — {currentScripture.reference}
        </p>
      </div>
    </div>
  );
}
